import * as SwitchPrimitive from '@radix-ui/react-switch';
import { useId, type ReactNode } from 'react';
import { cn, disabledStyles, focusRing } from './utils';

export interface SwitchProps {
  readonly checked: boolean;
  readonly onCheckedChange: (checked: boolean) => void;
  readonly label: ReactNode;
  readonly description?: ReactNode;
  readonly disabled?: boolean;
  readonly name?: string;
  readonly className?: string;
  /** Textos de estado exibidos ao lado (ex.: "Ativo"/"Inativo"). */
  readonly stateLabels?: { readonly on: string; readonly off: string };
}

/**
 * Switch (liga/desliga).
 *
 * WCAG 1.4.1 — quando `stateLabels` é fornecido, o estado aparece por TEXTO
 * além da posição do controle. Usamos isso em toda configuração que altera
 * comportamento regulado (autoaprovação, MFA obrigatório, exigir ciência):
 * ali "está ligado ou desligado?" não pode depender de enxergar a cor.
 */
export function Switch({
  checked,
  onCheckedChange,
  label,
  description,
  disabled = false,
  name,
  className,
  stateLabels,
}: SwitchProps) {
  const baseId = useId();
  const controlId = `${baseId}-switch`;
  const descriptionId = `${baseId}-description`;

  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <div className="min-w-0 flex-1">
        <label htmlFor={controlId} className="cursor-pointer text-sm font-medium text-content">
          {label}
        </label>
        {description ? (
          <p id={descriptionId} className="mt-0.5 text-xs text-content-subtle">
            {description}
          </p>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {stateLabels ? (
          <span
            aria-hidden="true"
            className={cn(
              'text-xs font-medium',
              checked ? 'text-success-content' : 'text-content-subtle',
            )}
          >
            {checked ? stateLabels.on : stateLabels.off}
          </span>
        ) : null}
        <SwitchPrimitive.Root
          id={controlId}
          name={name}
          checked={checked}
          onCheckedChange={onCheckedChange}
          disabled={disabled}
          aria-describedby={description ? descriptionId : undefined}
          className={cn(
            'relative h-6 w-11 shrink-0 rounded-full border border-transparent transition-colors',
            'bg-border-strong data-[state=checked]:bg-brand',
            focusRing,
            disabledStyles,
          )}
        >
          <SwitchPrimitive.Thumb
            className={cn(
              'block h-5 w-5 translate-x-0.5 rounded-full bg-surface shadow-sm transition-transform',
              'data-[state=checked]:translate-x-[1.375rem]',
            )}
          />
        </SwitchPrimitive.Root>
      </div>
    </div>
  );
}
